import { Inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
} from 'rxjs/operators';
import {
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  OrganizationalContainer,
  OrganizationalContainerSelectionService,
  TOKENS as TOKENS_MASTERDATA,
} from '@trustedshops/tswp-core-masterdata';
import { RxJsBridge } from '@trustedshops/tswp-core-common-eventing-rxjs';
import { ANGULAR_HTTP_CLIENT_TOKEN } from '../angular-http-client.module';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ChannelSeoSettings } from '../models/channel-seo-settings';
import { ChannelInviteSettings } from '../models/channel-invites-settings';
import { ReviewsService } from './reviews.service';
import { AccountService } from './account.service';

/**
 * @alias ChannelsService
 * @description service to operate with Account's Channels data
 */
@Injectable()
export class ChannelsService {
  private static readonly TYPE: string = 'ChannelsService';
  public readonly selectedChannel$: Observable<
    OrganizationalContainer<unknown>
  >;
  public readonly selectedChannelId$: Observable<string>;
  public selectedChannelSEOSettings$: Observable<ChannelSeoSettings>;
  public readonly selectedChannelHasSeoSettingsSet$: Observable<
    boolean | undefined
  >;
  public readonly selectedChannelHasInvitesReminderSettingsEnabled$: Observable<
    boolean | undefined
  >;
  public readonly selectedChannelReviewsCount$: Observable<number>;
  public readonly selectedChannelHasReviews$: Observable<boolean>;

  public readonly selectedChannelMonthlyReviewGoal$: Observable<number>;
  public readonly selectedChannelHasMonthlyReviewGoal$: Observable<boolean>;

  private readonly channelSeoSettingsBaseUrl =
    environment.apis.channelSeoSettings;
  private readonly channelInvitesReminderSettingsBaseUrl =
    environment.apis.channelInvitesReminder;

  public constructor(
    @Inject(TOKENS_MASTERDATA.OrganizationalContainerSelectionService)
    private organizationalContainerSelector: OrganizationalContainerSelectionService,
    @Inject(COMMON_TOKENS.LogService) private readonly logService: LogService,
    @Inject(ANGULAR_HTTP_CLIENT_TOKEN) private readonly httpClient: HttpClient,
    private readonly accountService: AccountService,
    private readonly reviewsService: ReviewsService
  ) {
    this.selectedChannel$ = this.getChannels().pipe(
      filter((channels) => channels?.length > 0),
      distinctUntilChanged((previousChannels, currentChannels) => {
        if (previousChannels.length !== currentChannels.length) {
          return false;
        }

        return previousChannels.every(
          (item, index) => item.id === currentChannels[index].id
        );
      }),
      map((channels) => channels[0]),
      catchError((error) => {
        this.logService.error(
          ChannelsService.TYPE,
          new Error(`Get Selected Channel has failed: ${JSON.stringify(error)}`)
        );

        return of(undefined);
      }),
      shareReplay(1)
    );

    this.selectedChannelId$ = this.selectedChannel$.pipe(
      map((selectedChannel) => selectedChannel?.id)
    );
    this.selectedChannelSEOSettings$ = this.selectedChannelId$.pipe(
      switchMap((channelId) => this.getSeoSettings(channelId))
    );
    this.selectedChannelHasSeoSettingsSet$ =
      this.selectedChannelSEOSettings$.pipe(
        map((settings) => this.hasSeoSettingsSet(settings)),
        catchError(() => of(undefined)),
        shareReplay(1)
      );

    this.selectedChannelHasInvitesReminderSettingsEnabled$ =
      this.selectedChannelId$.pipe(
        switchMap((channelId) => this.getInvitesReminderSettings(channelId)),
        map((settings: ChannelInviteSettings[]) =>
          this.hasInvitesReminderSettingsEnabled(settings)
        ),
        catchError(() => of(undefined)),
        shareReplay(1)
      );

    this.selectedChannelReviewsCount$ = this.selectedChannelId$.pipe(
      distinctUntilChanged(),
      map((channelId) => new HttpParams({ fromObject: { channelId } })),
      switchMap((params) => this.reviewsService.getReviewsCount(params)),
      map((response) => response?.items?.length ?? 0),
      shareReplay(1)
    );

    this.selectedChannelHasReviews$ = this.selectedChannelReviewsCount$.pipe(
      map((count) => count > 0),
      shareReplay(1)
    );

    this.selectedChannelMonthlyReviewGoal$ = combineLatest([
      this.selectedChannelId$,
      this.accountService.accountId$.pipe(
        filter((accountId): accountId is string => !!accountId)
      ),
    ]).pipe(
      distinctUntilChanged(),
      map(
        ([channelId, accountId]) =>
          new HttpParams({ fromObject: { channelId, accountId } })
      ),
      switchMap((params) =>
        this.reviewsService.getMonthlyReviewsCountGoal(params)
      ),
      map((response) => response?.value ?? undefined),
      shareReplay(1)
    );

    this.selectedChannelHasMonthlyReviewGoal$ =
      this.selectedChannelMonthlyReviewGoal$.pipe(
        map((goal) => goal > 0),
        shareReplay(1)
      );
  }

  public getChannels(): Observable<OrganizationalContainer<unknown>[]> {
    return this.organizationalContainerSelector
      .getSelection()
      .convertWith(RxJsBridge(BehaviorSubject))
      .pipe(
        catchError((error) => {
          this.logService.error(
            ChannelsService.TYPE,
            new Error(`Get Channels has failed: ${JSON.stringify(error)}`)
          );

          return throwError(() => error);
        })
      );
  }

  public getSeoSettings(channelId: string): Observable<ChannelSeoSettings> {
    return this.httpClient
      .get<ChannelSeoSettings>(
        `${this.channelSeoSettingsBaseUrl}/shop-profiles/${channelId}`
      )
      .pipe(
        catchError((error) => {
          this.logService.error(
            ChannelsService.TYPE,
            new Error(`Get Seo Settings has failed: ${JSON.stringify(error)}`)
          );

          return throwError(() => error);
        })
      );
  }

  public getInvitesReminderSettings(
    channelId: string
  ): Observable<ChannelInviteSettings[]> {
    return this.httpClient
      .get(`${this.channelInvitesReminderSettingsBaseUrl}/event-types`)
      .pipe(
        switchMap((eventsTypes) =>
          this.httpClient.get<ChannelInviteSettings[]>(
            `${this.channelInvitesReminderSettingsBaseUrl}/invite-settings`,
            {
              params: new HttpParams({
                fromObject: {
                  'channel-id': channelId,
                  'event-type-id': eventsTypes?.[0]?.id,
                },
              }),
            }
          )
        ),
        catchError((error) => {
          this.logService.error(
            ChannelsService.TYPE,
            new Error(
              `Get Invites Reminder Settings has failed: ${JSON.stringify(
                error
              )}`
            )
          );

          return throwError(() => error);
        })
      );
  }

  private hasSeoSettingsSet(seoSettings: ChannelSeoSettings): boolean {
    return (
      seoSettings?.categories?.length > 0 ||
      seoSettings?.description?.length > 0 ||
      seoSettings?.socialMediaLinks?.length > 0
    );
  }

  private hasInvitesReminderSettingsEnabled(
    settings: ChannelInviteSettings[]
  ): boolean {
    return settings?.[0]?.serviceInviteConfiguration?.enabled === true;
  }
}
